import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { PlusIcon, GlobeIcon, MessageCircleIcon, PhoneIcon, CodeIcon, FacebookIcon, InstagramIcon, SendIcon, SmartphoneIcon, QrCodeIcon, SparklesIcon, BotIcon, ArrowRightIcon, TrendingUpIcon, ClockIcon } from 'lucide-react';
import { WidgetBuilder } from './WidgetBuilder';
interface Agent {
  name: string;
  channel: string;
  icon: React.ReactNode;
  status: 'Active';
  conversations: number;
  leads: number;
  lastActivity: string;
}
const CHANNELS = [{
  id: 'Website Widget',
  icon: <GlobeIcon className="w-6 h-6" data-id="element-66" data-mp="4gm4hu9otkft" />,
  desc: 'Embed an AI assistant on your website.',
  available: true
}, {
  id: 'WhatsApp Agent',
  icon: <MessageCircleIcon className="w-6 h-6" data-id="element-67" data-mp="3vnynmeu4s7yd" />,
  desc: 'Answer customers on WhatsApp.',
  available: false
}, {
  id: 'Voice Agent',
  icon: <PhoneIcon className="w-6 h-6" data-id="element-68" data-mp="219nye59mjbs2" />,
  desc: 'AI phone calls and voice support.',
  available: false
}, {
  id: 'Custom API',
  icon: <CodeIcon className="w-6 h-6" data-id="element-69" data-mp="3balvgnv3x96l" />,
  desc: 'Integrate the agent anywhere.',
  available: false
}, {
  id: 'Facebook Messenger',
  icon: <FacebookIcon className="w-6 h-6" data-id="element-70" data-mp="3l7i54wwqs1cq" />,
  desc: 'Reply on Messenger.',
  available: false
}, {
  id: 'Instagram',
  icon: <InstagramIcon className="w-6 h-6" data-id="element-71" data-mp="3pz03rebalpuj" />,
  desc: 'Handle Instagram DMs.',
  available: false
}, {
  id: 'Telegram',
  icon: <SendIcon className="w-6 h-6" data-id="element-72" data-mp="3u1mqj0t3wkkv" />,
  desc: 'Deploy on Telegram.',
  available: false
}, {
  id: 'Mobile SDK',
  icon: <SmartphoneIcon className="w-6 h-6" data-id="element-73" data-mp="7slgupv1q5k0" />,
  desc: 'In-app assistant for mobile.',
  available: false
}, {
  id: 'QR Code Assistant',
  icon: <QrCodeIcon className="w-6 h-6" data-id="element-74" data-mp="cqwrh2t60k3a" />,
  desc: 'Scan-to-chat in physical spaces.',
  available: false
}];
export function BusinessAIAgents() {
  const [view, setView] = useState<'home' | 'choose'>('home');
  const [builderOpen, setBuilderOpen] = useState(false);
  const [builderStep, setBuilderStep] = useState<string | undefined>(undefined);
  const [agents, setAgents] = useState<Agent[]>([]);
  const openBuilder = (step?: string) => {
    setBuilderStep(step);
    setBuilderOpen(true);
  };
  const deploy = () => {
    setAgents([{
      name: 'Talab Assistant',
      channel: 'Website Widget',
      icon: <GlobeIcon className="w-5 h-5" data-id="element-75" data-mp="2bj9mgvq7q9qh" />,
      status: 'Active',
      conversations: 327,
      leads: 18,
      lastActivity: '12 minutes ago'
    }]);
    setBuilderOpen(false);
    setView('home');
  };
  if (builderOpen) {
    return <div className="w-full max-w-6xl mx-auto pb-12" data-id="element-76" data-mp="2z5pw62u1nl28">
        <WidgetBuilder onBack={() => setBuilderOpen(false)} onDeploy={deploy} initialStep={builderStep} data-id="element-77" data-mp="2u7of1ng6hvte" />
      </div>;
  }
  return <div className="w-full max-w-6xl mx-auto pb-24" data-id="element-78" data-mp="387pecygsov6r">
      {/* Header */}
      <div className="flex items-start justify-between mb-8 gap-4" data-id="element-79" data-mp="3475irzbvdzmq">
        <div data-id="element-80" data-mp="3mtmt6kizmv1n">
          <h2 className="text-2xl font-display font-bold text-dark-navy" data-id="element-81" data-mp="3mtwkqcjola9g">
            AI Agents
          </h2>
          <p className="text-sm text-slate-500" data-id="element-82" data-mp="3rs6csea3zuqu">
            Deploy your AI assistant anywhere your customers interact with your
            business.
          </p>
        </div>
        {agents.length > 0 && <button onClick={() => setView('choose')} className="px-4 py-2.5 bg-primary-indigo text-white text-sm font-bold rounded-xl hover:bg-indigo-700 flex items-center gap-2 flex-shrink-0" data-id="element-83" data-mp="3iqzvi2zmyfwj">
            <PlusIcon className="w-4 h-4" data-id="element-84" data-mp="38w7zv4pm634e" /> Create Agent
          </button>}
      </div>

      {/* Empty state */}
      {agents.length === 0 && <motion.div initial={{
      opacity: 0,
      y: 12
    }} animate={{
      opacity: 1,
      y: 0
    }} className="flex flex-col items-center text-center py-16" data-id="element-85" data-mp="2z5lm7g1gjmoc">
          <div className="relative mb-8" data-id="element-86" data-mp="3shabb8inde8a">
            <div className="w-28 h-28 rounded-3xl bg-gradient-to-br from-primary-indigo to-ai-purple flex items-center justify-center shadow-xl" data-id="element-87" data-mp="3sg8ok6s9r3aq">
              <BotIcon className="w-14 h-14 text-white" data-id="element-88" data-mp="3oi2kj6itf1ti" />
            </div>
            <motion.div animate={{
          scale: [1, 1.15, 1],
          opacity: [0.6, 0.2, 0.6]
        }} transition={{
          duration: 2.5,
          repeat: Infinity
        }} className="absolute inset-0 rounded-3xl bg-primary-indigo/30 blur-xl -z-10" data-id="element-89" data-mp="3jnf1voaht1mu" />
            <div className="absolute -right-3 -top-3 w-9 h-9 rounded-full bg-white shadow-md flex items-center justify-center" data-id="element-90" data-mp="30pm4f1in1jwu">
              <SparklesIcon className="w-5 h-5 text-ai-purple" data-id="element-91" data-mp="2vsccnf7s0w5u" />
            </div>
          </div>
          <h3 className="text-2xl font-display font-bold text-dark-navy mb-2" data-id="element-92" data-mp="yxg2w4bfe2wd">
            Your AI is ready to be deployed
          </h3>
          <p className="text-slate-500 max-w-md mb-8" data-id="element-93" data-mp="txtp9troqkn1">
            Connect customer touchpoints and let Talab answer questions, qualify
            leads, and generate opportunities automatically.
          </p>
          <button onClick={() => setView('choose')} className="px-6 py-3 bg-primary-indigo text-white font-bold rounded-xl hover:bg-indigo-700 transition-colors shadow-md flex items-center gap-2" data-id="element-94" data-mp="1b90cl1il3pei">
            <PlusIcon className="w-5 h-5" data-id="element-95" data-mp="ilctxs0vj8ui" /> Create Agent
          </button>
        </motion.div>}

      {/* Active state */}
      {agents.length > 0 && <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-96" data-mp="siilf1deh1v9">
          {agents.map(a => <div key={a.name} className="bg-white border border-slate-200 rounded-2xl p-5 shadow-soft hover:shadow-medium transition-shadow" data-id="element-97" data-mp="wju9v3kjfh1v">
              <div className="flex items-start justify-between mb-4" data-id="element-98" data-mp="27z96u7cqy8wz">
                <div className="flex items-center gap-3" data-id="element-99" data-mp="27xg24xlp1yrm">
                  <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-primary-indigo to-ai-purple flex items-center justify-center text-white" data-id="element-100" data-mp="8tpyhmeioqse">
                    {a.icon}
                  </div>
                  <div data-id="element-101" data-mp="hu4lqrb1kfyj">
                    <div className="font-bold text-dark-navy leading-tight" data-id="element-102" data-mp="hunw9myd422j">
                      {a.name}
                    </div>
                    <div className="text-xs text-slate-500" data-id="element-103" data-mp="3lbw91se8gbqe">{a.channel}</div>
                  </div>
                </div>
                <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-green-100 text-green-700 text-xs font-bold rounded-full" data-id="element-104" data-mp="3hauidzydt801">
                  <span className="w-1.5 h-1.5 rounded-full bg-green-500" data-id="element-105" data-mp="3v3ad8dh4pzpv" />{' '}
                  {a.status}
                </span>
              </div>
              <div className="grid grid-cols-2 gap-3 mb-4" data-id="element-106" data-mp="32l54d456x9ic">
                <div className="bg-slate-50 rounded-xl p-3" data-id="element-107" data-mp="32jljf08laaa8">
                  <div className="text-xl font-display font-bold text-dark-navy" data-id="element-108" data-mp="2xlad2zhi6hwr">
                    {a.conversations}
                  </div>
                  <div className="text-xs text-slate-500" data-id="element-109" data-mp="1d26kfy5nzo98">Conversations</div>
                </div>
                <div className="bg-slate-50 rounded-xl p-3" data-id="element-110" data-mp="1hx43lxwnrdzu">
                  <div className="text-xl font-display font-bold text-dark-navy" data-id="element-111" data-mp="144nxupcdh1v8">
                    {a.leads}
                  </div>
                  <div className="text-xs text-slate-500" data-id="element-112" data-mp="190lcmr7zm05w">Leads generated</div>
                </div>
              </div>
              <div className="text-xs text-slate-400 flex items-center gap-1 mb-3" data-id="element-113" data-mp="20tvfvnds8sue">
                <ClockIcon className="w-3.5 h-3.5" data-id="element-114" data-mp="20rj2zomvt8o7" /> {a.lastActivity}
              </div>
              <div className="flex items-center gap-2" data-id="element-115" data-mp="1mw6wyl39zvrz">
                <button onClick={() => openBuilder('deploy')} className="flex-1 px-3 py-2 bg-primary-indigo text-white text-sm font-bold rounded-lg hover:bg-indigo-700 flex items-center justify-center gap-1.5" data-id="element-116" data-mp="1rse24bnul38v">
                  <CodeIcon className="w-4 h-4" data-id="element-117" data-mp="181lib3yqgmui" /> Get install code
                </button>
                <button onClick={() => openBuilder()} className="px-3 py-2 bg-white border border-slate-200 text-slate-700 text-sm font-medium rounded-lg hover:bg-slate-50 flex items-center gap-1.5" data-id="element-118" data-mp="17z8s63itn66d">
                  Manage
                </button>
              </div>
            </div>)}

          {/* Coming soon agents */}
          {[{
        n: 'WhatsApp Agent',
        i: <MessageCircleIcon className="w-5 h-5" data-id="element-119" data-mp="1lub3552izlq1" />
      }, {
        n: 'Voice Agent',
        i: <PhoneIcon className="w-5 h-5" data-id="element-120" data-mp="1h0gq2khfe4zx" />
      }].map(c => <div key={c.n} className="bg-slate-50 border border-dashed border-slate-200 rounded-2xl p-5 flex flex-col items-start opacity-80" data-id="element-121" data-mp="1c2nh5joqmwvy">
              <div className="flex items-center gap-3 mb-4" data-id="element-122" data-mp="24r2jb5mrtkpv">
                <div className="w-11 h-11 rounded-xl bg-white border border-slate-200 flex items-center justify-center text-slate-400" data-id="element-123" data-mp="2d0rroyc00tu8">
                  {c.i}
                </div>
                <div data-id="element-124" data-mp="2h3ws8vciqlg8">
                  <div className="font-bold text-slate-600 leading-tight" data-id="element-125" data-mp="2m0n9hm027n38">
                    {c.n}
                  </div>
                  <div className="text-xs text-slate-400" data-id="element-126" data-mp="2r1aw7vh99hrn">Multi-channel</div>
                </div>
              </div>
              <span className="px-2.5 py-1 bg-slate-200 text-slate-500 text-xs font-bold rounded-full mt-auto" data-id="element-127" data-mp="2vyumjtr89kzy">
                Coming Soon
              </span>
            </div>)}
        </div>}

      {/* Channel chooser */}
      <AnimatePresence data-id="element-128" data-mp="39qradllv58qj">
        {view === 'choose' && <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-id="element-129" data-mp="39q99ucs648ct">
            <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm" onClick={() => setView('home')} data-id="element-130" data-mp="3er59ge5hl8cf" />
            <motion.div initial={{
          opacity: 0,
          scale: 0.96,
          y: 20
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.96
        }} className="relative w-full max-w-3xl bg-white rounded-2xl shadow-2xl overflow-hidden max-h-[90vh] flex flex-col" data-id="element-131" data-mp="1qgv69ze6yzzu">
              <div className="px-6 py-4 border-b border-slate-100" data-id="element-132" data-mp="2e6fqsmpzu5b8">
                <h2 className="text-lg font-display font-bold text-dark-navy" data-id="element-133" data-mp="2e4miz43n9f1n">
                  Choose a channel
                </h2>
                <p className="text-sm text-slate-500" data-id="element-134" data-mp="2n6vo1eg58g7n">
                  Where should your AI employee work?
                </p>
              </div>
              <div className="p-6 overflow-y-auto grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-id="element-135" data-mp="nvkv30m9etde">
                {CHANNELS.map(ch => <button key={ch.id} disabled={!ch.available} onClick={() => {
              if (ch.available) {
                setView('home');
                setBuilderOpen(true);
              }
            }} className={`text-left rounded-2xl p-4 border-2 transition-colors relative ${ch.available ? 'border-slate-200 hover:border-primary-indigo bg-white' : 'border-slate-100 bg-slate-50 cursor-not-allowed'}`} data-id="element-136" data-mp="l9brdh6oxvex">
                    <div className={`w-11 h-11 rounded-xl flex items-center justify-center mb-3 ${ch.available ? 'bg-indigo-50 text-primary-indigo' : 'bg-white text-slate-300'}`} data-id="element-137" data-mp="za60y35h62f6">
                      {ch.icon}
                    </div>
                    <div className={`font-bold mb-0.5 ${ch.available ? 'text-dark-navy' : 'text-slate-500'}`} data-id="element-138" data-mp="3f1aesej3r0id">
                      {ch.id}
                    </div>
                    <div className="text-xs text-slate-400 leading-snug" data-id="element-139" data-mp="3cgm3of6afbf4">
                      {ch.desc}
                    </div>
                    {!ch.available && <span className="absolute top-3 right-3 px-2 py-0.5 bg-slate-200 text-slate-500 text-[10px] font-bold rounded-full" data-id="element-140" data-mp="32iokw77766tw">
                        Coming Soon
                      </span>}
                  </button>)}
              </div>
            </motion.div>
          </div>}
      </AnimatePresence>
    </div>;
}